/**
 * frontend/src/store/SettingsStore.js
 *
 * All user preference state.
 * Revealing Module Pattern with observer pattern for reactivity.
 * Persists to localStorage — settings survive page reload.
 *
 * No React state. No Redux. Plain JS — usable from any component.
 * React hooks subscribe via useSettings() hook.
 */

const SettingsStore = (() => {

    // ── Default settings ──────────────────────────────────────────────────────
    // Every user-configurable value lives here.
    // Nothing is hardcoded in components — they read from here.

    const _defaults = {
        // Appearance
        theme:            'dark',           // 'dark' | 'light' | 'system'
        accentColor:      'gold',           // 'gold' | 'blue' | 'red' | 'green'
        fontSize:         'medium',         // 'small' | 'medium' | 'large'
        defaultScript:    'iast',     // 'devanagari' | 'iast' | 'telugu'

        // Search behaviour
        defaultLanguage:      'auto',       // 'auto' | 'sanskrit' | 'telugu' | 'english'
        defaultTradition:     'all',
        resultsPerPage:       10,
        showIAST:             true,
        showSemanticScore:    true,
        showTelugu:           false,
        enableCrossEncoder:   false,        // v2 feature, off by default

        // Corpus
        includeSanskrit: true,
        includeTelugu:   true,

        // Advanced / developer
        apiEndpoint:        '',             // empty = use REACT_APP_API_URL
        topK:               20,
        debugMode:          false,
        enableQueryLogging: true,

        // Onboarding
        hasSeenWelcome:     false,
    };

    // ── Private state ─────────────────────────────────────────────────────────

    const _STORAGE_KEY = 'vakya_settings_v1';
    let _settings = { ..._defaults };
    const _listeners = new Set();

    // ── Private helpers ───────────────────────────────────────────────────────

    function _load() {
        try {
            const raw = localStorage.getItem(_STORAGE_KEY);
            if (raw) {
                const saved = JSON.parse(raw);
                // Merge saved over defaults — new keys from app updates get defaults
                _settings = { ..._defaults, ...saved };
            }
        } catch (e) {
            console.warn('SettingsStore: Failed to load from localStorage', e);
            _settings = { ..._defaults };
        }
    }

    function _save() {
        try {
            localStorage.setItem(_STORAGE_KEY, JSON.stringify(_settings));
        } catch (e) {
            console.warn('SettingsStore: Failed to save to localStorage', e);
        }
    }

    function _notify() {
        _listeners.forEach(cb => cb({ ..._settings }));
    }

    function _applyThemeToDom(theme) {
        const resolved = theme === 'system'
            ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
            : theme;
        document.documentElement.setAttribute('data-theme', resolved);
    }

    // ── Public interface ──────────────────────────────────────────────────────

    function get(key) {
        return key ? _settings[key] : { ..._settings };
    }

    function set(key, value) {
        if (!(key in _defaults)) {
            console.warn(`SettingsStore: Unknown key "${key}"`);
            return;
        }
        _settings[key] = value;
        _save();
        _notify();

        // Side effects for specific settings
        if (key === 'theme') _applyThemeToDom(value);
        if (key === 'apiEndpoint' && value) {
            // Dynamically reconfigure API service when endpoint changes
            import('../services/ApiService').then(m => {
                m.default.configure({ baseUrl: value });
            });
        }
    }

    function setMany(updates) {
        Object.assign(_settings, updates);
        _save();
        _notify();
        if (updates.theme) _applyThemeToDom(updates.theme);
    }

    function reset() {
        _settings = { ..._defaults };
        _save();
        _notify();
        _applyThemeToDom(_defaults.theme);
    }

    function subscribe(callback) {
        _listeners.add(callback);
        return () => _listeners.delete(callback); // returns unsubscribe function
    }

    function getSearchParams() {
        /**
         * Returns the subset of settings that map to SearchRequest params.
         * Used by search components to build the default query.
         */
        return {
            language:       _settings.defaultLanguage,
            tradition:      _settings.defaultTradition,
            page_size:      _settings.resultsPerPage,
            include_iast:   _settings.showIAST,
            include_telugu: _settings.showTelugu,
            min_score:      0.0,
        };
    }

    function getResolvedTheme() {
        if (_settings.theme === 'system') {
            return window.matchMedia('(prefers-color-scheme: dark)').matches
                ? 'dark'
                : 'light';
        }
        return _settings.theme;
    }

    // ── Init ──────────────────────────────────────────────────────────────────
    _load();
    _applyThemeToDom(_settings.theme);

    // React to system theme changes when theme = 'system'
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        if (_settings.theme === 'system') {
            _applyThemeToDom('system');
            _notify();
        }
    });

    return {
        get,
        set,
        setMany,
        reset,
        subscribe,
        getSearchParams,
        getResolvedTheme,
    };

})();

export default SettingsStore;
